/* eslint-disable no-use-before-define */
import uuid from 'uuid/v4';
import { generatePath } from 'react-router';
import htmlStripsService from '@Services/HtmlStripsService';
import HistoryResource from '@Services/HistoryResource';
import AppStorage from '@Services/AppStorage';
import Catalyst from '@Services/Catalyst';
import Breadcrumb from '@Services/Breadcrumb';
import { open as openStructureDialog } from '@Containers/StructureDialog/StructureDialogAction';
import store from '@Store';
import { generateResultsPath, generateHeatmapPath } from '@AppRoutes/utils';
import history from '@AppRoutes/history';
import { paths } from '@AppRoutes/constants';
import { getCombinedRouteParams } from '@AppRoutes/AppRoutesSelectors';
import * as AuthUtils from '@Services/Auth/AuthUtils';
import NotifyDialogsService from '@Containers/Dialogs/NotifyDialogsService';
import UserDatabases from '@Services/UserDatabases';
import HistoryHelper from '@Services/HistoryHelper';
import { dispatchOpenViewSaveModal, dispatchOpenConfirmationModal, closeConfirmationModal } from '@Pages/History/HistoryAction';

import get from 'lodash/get';
import each from 'lodash/each';
import assignIn from 'lodash/assignIn';
import flow from 'lodash/fp/flow';
import groupBy from 'lodash/fp/groupBy';
import map from 'lodash/fp/map';
import toArray from 'lodash/fp/toArray';
import compact from 'lodash/fp/compact';
import sortBy from 'lodash/fp/sortBy';
import moment from 'moment-timezone';

export const getHistoryTypes = () => HistoryHelper.getHistoryTypes();

export function formatNewHistory(...args) {
  return HistoryHelper.formatNewHistory(...args);
}

export function storeRecentZeroSearch(result, historyType, factItem) {
  const currentUser = AuthUtils.getUser();
  const recentSearches = get(currentUser, 'history.recentSearches', []);

  const zeroResult = {
    context: result.payload.context,
    databaseId: result.payload.databaseId,
    queryId: result.payload.queryId,
    resultName: result.payload.resultName,
    searchId: result.payload.searchId,
    hitSetKey: result.payload.hitSetKey,
    isFromAdvancedSearch: true,
    totalResult: result.payload.resultSize,
    creationTime: new Date().valueOf(),
    queryFactItem: factItem,
    historyType,
    resultSize: result.payload.resultSize,
    userTime: moment().format('YYYY/MM/DD HH:mm:ss'),
  };

  recentSearches.push(zeroResult);
  recentSearches.sort((x, y) => x.creationTime - y.creationTime);

  AuthUtils.updateUser({ history: currentUser.history });
}

/**
 * @ngdoc method
 * @name restoreHistory
 * @description retrieves actual hitset name and hitset key for saved history item
 * */
export function restoreHistory(item) {
  return HistoryResource.restoreHistory(item)
    .then((result) => {
      // eslint-disable-next-line no-param-reassign
      item.resultName = result.resultName;
      // eslint-disable-next-line no-param-reassign
      item.hitSetKey = result.hitSetKey;
      // eslint-disable-next-line no-param-reassign
      item.resultSize = result.resultSize;

      return item;
    })
    .catch(Logger.reject);
}

export function requestSavedHistory() {
  return HistoryResource.getSavedHistory().then(processHistoryItems).catch(Logger.reject);
}

export function processHistoryItems(historyItems) {
  historyItems.forEach((item) => {
    if (item.queryFactItem) {
      // eslint-disable-next-line no-param-reassign
      item.structures = HistoryHelper.extractStructuresFromFacts(item.queryFactItem);
    }
  });

  return splitHistoryByDBs(historyItems);
}

/**
 * @ngdoc method
 * @name formatExistingHistory
 * @description format object before saving to save in BE
 * */
export function formatExistingHistory(historyItem, newSearchName) {
  // eslint-disable-next-line no-param-reassign
  historyItem.searchName = newSearchName;
  return historyItem;
}

export function goToResultPage(item) {
  const userHistory = AuthUtils.getUser().history;
  const prevStateParams = getCombinedRouteParams(store.getState());
  if (!item) {
    const breadcrumbs = Breadcrumb.retrieveBreadcrumbs(userHistory.viewedResultsParams.databaseId);
    // eslint-disable-next-line no-param-reassign
    item = breadcrumbs.find((crumb) => crumb.active === true);
  }
  // RXFE-4881: for big results breadcrumb can be not created at time
  // RXFE-4947: was added new param "referrer"(RXFE-4885) and check only stateParams is not working
  if (
    item ||
    (prevStateParams && prevStateParams.databaseId && prevStateParams.context && prevStateParams.resultName && prevStateParams.searchId)
  ) {
    const stateParams = {
      databaseId: item ? item.databaseId : prevStateParams.databaseId,
      context: item ? item.context : prevStateParams.context,
      resultName: item ? item.resultName : prevStateParams.resultName,
      searchId: item ? item.searchId : prevStateParams.searchId,
      hitSetKey: item ? item.hitSetKey : prevStateParams.hitSetKey,
      fromContext: item ? item.fromContext : prevStateParams.fromContext,
      limitOrExclude: item ? item.limitOrExclude : prevStateParams.limitOrExclude,
    };

    if (item.isHeatmapShown) {
      assignIn(stateParams, {
        context: 'dpitems',
        resultName: item ? item.heatmapResultName : prevStateParams.resultName,
        index: item ? item.index : prevStateParams.index,
        isRow: item ? item.isRow : prevStateParams.isRow,
        isHeatmap: true,
        ascending: item ? item.ascending : prevStateParams.ascending,
        sortedMatrixName: item ? item.sortedMatrixName : prevStateParams.sortedMatrixName,
      });
      const pathName = generateHeatmapPath(stateParams);
      history.push({
        pathname: pathName,
        state: stateParams,
      });
    } else {
      assignIn(stateParams, {
        pageNumber: 1,
        order: item ? item.order : prevStateParams.order,
        sort: item ? item.sort : prevStateParams.sort,
      });
      const pathName = generateResultsPath(stateParams);
      history.push({
        pathname: pathName,
        state: stateParams,
      });
    }
  }
}

export function showStructurePopup(structure) {
  store.dispatch(openStructureDialog(uuid(), structure));
}

export function openConfirmPopup(content, opts = {}) {
  return new Promise((resolve, reject) => {
    dispatchOpenConfirmationModal({
      ...content,
      ...opts,
      onClose: () => {
        reject();
        store.dispatch(closeConfirmationModal());
      },
      onConfirm: () => {
        resolve();
        store.dispatch(closeConfirmationModal());
      },
    });
  });
}

/* ----------------------- RECENT HISTORY --------------------------- */
/**
 * @ngdoc method
 * @name retrieveRecentHistory
 * @description retrieve the recent searches (including 0 results) that is in the appStorage for the current user
 * @returns {Array} all recent searches of user
 * */
export function retrieveRecentHistory() {
  return get(AuthUtils.getUser(), 'history.recentSearches', []);
}

/**
 * @ngdoc method
 * @name retrieveRecentHistoryWithoutZeroResults
 * @description retrieve the recent searches (without 0 results) that is in the appStorage for the current user
 * @returns {Array} all recent searches of user without 0 results
 * */
export function retrieveRecentHistoryWithoutZeroResults() {
  return get(AuthUtils.getUser(), 'history.recentSearches', []).filter((search) => search.resultSize !== 0);
}

/**
 * @description store breadcrumbs array in recentSearches so that they don't
 * get lost when the breadcrumbs are reset
 * */
export function storeRecentHistory(sort, order) {
  const activeDatabaseId = AuthUtils.getUser().history.viewedResultsParams.databaseId;
  const activeBreadcrumb = Breadcrumb.getActiveBreadcrumbForDb(activeDatabaseId);
  // HistoryHelper.storeRecentHistory({ ...activeBreadcrumb, sort, order });
  const historyItem = {
    ...activeBreadcrumb,
    ...(sort !== undefined && { sort }),
    ...(order !== undefined && { order }),
  };
  HistoryHelper.storeRecentHistory(historyItem);
}

/* ----------------------- SAVED HISTORY --------------------------- */

export function openSaveHistoryModal(historyItem) {
  if (AuthUtils.isAnonymousUser()) {
    const errorTitle = 'Login required';
    const errorMessage = 'Please sign-in/register to save your History.';
    return NotifyDialogsService.openModalWindow({
      title: errorTitle,
      text: errorMessage,
      cancelText: 'Cancel',
      confirmText: 'Sign in',
      type: 'info',
      backdrop: 'static',
      onConfirm: () => {
        Catalyst.trackConversionDriverClick('history:savesearch:sign-in');
        AppStorage.instance.set('scheduledHistory', historyItem);
        history.push({
          pathname: generatePath(paths.LOGIN),
        });
      },
      onCancel: () => {
        AppStorage.instance.set('scheduledHistory', null);
      },
      trackingOptions: {
        message: `${errorTitle} ${errorMessage}`,
        component: 'History',
      },
    }).result;
  }
  const promise = new Promise(() => {
    dispatchOpenViewSaveModal({ historyItem });
    AppStorage.instance.set('scheduledHistory', null);
  });
  return promise;
}

export function getSavedHistory() {
  return HistoryResource.getSavedHistory();
}

export function removeHistory(historyItem) {
  return HistoryResource.removeHistory(historyItem);
}

/**
 * @ngdoc function
 * @name splitHistoryByDBs
 * @param {array} historyItems List of history items objects
 * @description Restructures history items array into the other array of databases,
 *              where each item contains databaseName it's own list of history items.
 *              Sorts databases in alphabetical order, keeping Reaxys DB on first position.
 */
export function splitHistoryByDBs(historyItems) {
  return flow([
    groupBy('databaseId'),
    map((items, key) => {
      const name = UserDatabases.getDbName(key);
      return name
        ? {
            databaseId: key,
            priority: UserDatabases.isDefaultDatabase(key) ? '' : name.toLowerCase(),
            databaseName: name,
            items,
          }
        : null;
    }),
    toArray,
    compact,
    sortBy('priority'),
  ])(historyItems);
}

export function updateItemsComments(databaseStorage) {
  each(databaseStorage, (database) => {
    // eslint-disable-next-line no-param-reassign,no-return-assign
    each(database.items, (item) => (item.comment = htmlStripsService.getTitleWithoutHtml(item.comment)));
  });
}
